import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/react'
import { IoTDeviceDirection, IotDeviceFunction, IotOutputFunction, SublocationWithDevices } from '@leid/api-locations'
import { OrderItem } from '@leid/api-orders'

import { LeidYellowButton } from 'components/ui/Buttons/Button'
import LeidModal from 'components/ui/Modal/LeidModal'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

import { DoorOutputConfig, DoorPINInfo } from './models'
import { Device, PinCodeForDeviceResponse } from '@leid/api-assets'
import { Loader } from 'components/ui/Loader'
import { LuDoorOpen } from 'react-icons/lu'
import { FaLockOpen } from 'react-icons/fa'
import { getPINCodeDates, toPrettifiedPin } from '../utils/devices'
import { pretifyDate } from 'utils/dates'

type Props = {
  isOpen: boolean
  onClose: () => void
  orderItem: OrderItem
  devices: SublocationWithDevices[]
}

const OpenDoorsModal = ({ isOpen, onClose, orderItem, devices }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [outDoors, setOutDoors] = useState<DoorOutputConfig[]>([])
  const [pinDoors, setPINDoors] = useState<DoorPINInfo[]>([])
  const [fetchedCode, setFetchedCode] = useState<PinCodeForDeviceResponse | null>(null)

  useEffect(() => {
    if (!isOpen) {
      resetToDefaultState()
      return
    }

    searchOutDoors()
  }, [isOpen])

  const resetToDefaultState = () => {
    setOutDoors([])
  }

  const searchOutDoors = async () => {
    setIsLoading(true)
    const outDoors: DoorOutputConfig[] = []
    const PINDoors: DoorPINInfo[] = []

    for (const sublocationData of devices) {
      const doorDevices = sublocationData.devices.filter((s) => s.function === IotDeviceFunction.DOOR)
      for (const door of doorDevices) {
        const outputs = door.outputs
          .filter(
            (s) =>
              s.function === IotOutputFunction.DOOR &&
              (s.direction === IoTDeviceDirection.OUT || s.direction === IoTDeviceDirection.BOTH) &&
              s.allowedToTriggerFromEcommerce === true,
          )
          .map<DoorOutputConfig>((s) => ({
            id: s.id,
            name: s.name,
            sublocationId: sublocationData.sublocationId,
          }))

        outDoors.push(...outputs)
      }

      if (sublocationData.assetDeviceId) {
        const sublocationDevices = await getSublocationDevices(sublocationData.assetDeviceId)
        if (!sublocationDevices) {
          // Error happened
          return undefined
        }

        PINDoors.push(
          ...sublocationDevices.map<DoorPINInfo>((s) => ({
            id: s.id,
            locationId: orderItem.locationId,
            name: s.name,
          })),
        )
      }
    }

    setOutDoors(outDoors)
    setPINDoors(PINDoors)
    setIsLoading(false)
  }

  const getSublocationDevices = async (assetDeviceId: number): Promise<Device[] | undefined> => {
    const response = await apiClient.getAssetDevices({
      ids: [assetDeviceId],
      active: true,
    })
    if (!response.success || !response.data) {
      console.log(`Error on getting sublocation devices`, assetDeviceId, response)
      return undefined
    }
    return response.data?.rows
  }

  const triggerOutput = async (sublocationId: number, gateId: number) => {
    setFetchedCode(null)

    const triggeredResponse = await apiClient.triggerSubLocationOutput({
      locationId: orderItem.locationId ?? 0,
      sublocationId: sublocationId,
      ioId: gateId ?? 0,
      userId: 0,
      orderId: orderItem.orderId,
      customerId: orderItem.customerId,
    })

    if (!triggeredResponse.success) {
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      console.error('Failed to trigger output')
      return
    }
    onClose()
  }

  const getPinCode = async (doorInfo: DoorPINInfo) => {
    setFetchedCode(null)

    const { startDate, endDate } = getPINCodeDates()

    const response = await apiClient.getPinCodeForDevice({
      deviceId: doorInfo.id,
      locationId: doorInfo.locationId,
      startDate,
      endDate,
      variance: 1,
    })

    if (!response.success || !response.data) {
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      console.error('Failed to get PIN')
      return false
    }

    setFetchedCode(response.data)
    return true
  }

  return (
    <LeidModal isOpen={isOpen} onClose={onClose} header={t('openLocation')}>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {fetchedCode && (
            <Box
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              flexWrap={'wrap'}
              width="100%"
              marginBottom={5}
            >
              <Flex flexDirection={'row'} alignItems={'center'} gap={3}>
                <FaLockOpen size={24} />
                <Text fontSize="xl" fontWeight="bold" textAlign="center">
                  {toPrettifiedPin(fetchedCode?.pinCode)}
                </Text>
              </Flex>
              <Text fontSize="xs" opacity={0.5}>{`${pretifyDate(fetchedCode.startDate)} | ${pretifyDate(
                fetchedCode.stopDate,
              )}`}</Text>
            </Box>
          )}

          <Flex flexDirection="column" justifyContent=" center" alignItems="center" gridGap="12px">
            {outDoors.map((door) => (
              <LeidYellowButton
                key={door.id}
                onClick={() => {
                  triggerOutput(door.sublocationId, door.id)
                }}
              >
                <LuDoorOpen size={20} />
                <Text>{door.name}</Text>
              </LeidYellowButton>
            ))}

            {pinDoors.map((door) => (
              <LeidYellowButton
                key={door.id}
                onClick={() => {
                  getPinCode(door)
                }}
              >
                <FaLockOpen size={20} />
                <Text>{door.name}</Text>
              </LeidYellowButton>
            ))}
          </Flex>
        </>
      )}
    </LeidModal>
  )
}

export default OpenDoorsModal
